import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { redux } from 'Core/redux';
import { PlantService } from 'Service/api/plant-service';
import { showToast } from 'Core/redux/toast/slice';

export const STORE_KEY = 'plant';
export const INITIAL_STATE = {
  data: [],
  loading: false,
  error: null,
  filters: {
    searchText: '',
  },
  selectedPlant: null,
  tileView: true,
  maintenanceRecords: [],
  selectedMaintenanceRecord: null,
  maintenanceData: null,
  cellHistoryData: [],
};

// Async thunks
export const fetchPlants = createAsyncThunk(
  'plant/fetchPlants',
  async (_, { rejectWithValue }) => {
    try {
      const response = await PlantService.getAllPlants();
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load Plants');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load Plants');
    }
  },
);

export const addPlant = createAsyncThunk(
  'plant/addPlant',
  async (plantPayload, { rejectWithValue }) => {
    try {
      const response = await PlantService.saveNewPlant(plantPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to add Plant');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add Plant');
    }
  },
);

export const editPlant = createAsyncThunk(
  'plant/editPlant',
  async (plantPayload, { rejectWithValue }) => {
    try {
      const response = await PlantService.saveNewPlant(plantPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to update Plant');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update Plant');
    }
  },
);

export const fetchPlantsWithFilters = () => (dispatch) => {
  dispatch(fetchPlants());
};

// Maintenance Records Thunks
export const fetchMaintenanceRecordsByPlantId = createAsyncThunk(
  'plant/fetchMaintenanceRecordsByPlantId',
  async (plantId, { rejectWithValue }) => {
    try {
      const response = await PlantService.getMaintenanceRecordsByPlantId(plantId);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load maintenance records');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load maintenance records');
    }
  },
);

export const fetchMaintenanceRecordsDetails = createAsyncThunk(
  'plant/fetchMaintenanceRecordsDetails',
  async (recordId, { rejectWithValue }) => {
    try {
      const response = await PlantService.getMaintenanceRecordDetails(recordId);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load maintenance record details');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load maintenance record details');
    }
  },
);

// Fetch plant by asset number for cell history
export const fetchPlantByAssetNumber = createAsyncThunk(
  'plant/fetchByAssetNumber',
  async (assetNumber) => {
    const { response } = await PlantService.getAllPlantByAssetNumber(assetNumber);
    return response;
  }
);

export const addPlantMaintenanceRecord = createAsyncThunk(
  'plant/addPlantMaintenanceRecord',
  async (maintenanceData, { rejectWithValue }) => {
    try {
      const response = await PlantService.addMaintenanceRecord(maintenanceData);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to add maintenance record');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add maintenance record');
    }
  },
);

export const { reducer, actions, caseReducers } = createSlice({
  name: 'plant',
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    resetFilters: state => {
      state.filters = INITIAL_STATE.filters;
    },
    setPlant: (state, action) => {
      state.selectedPlant = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload;
    },
    setMaintenanceData: (state, action) => {
      state.maintenanceData = action.payload;
    },
    clearMaintenanceData: (state) => {
      state.maintenanceData = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPlants.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlants.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchPlants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addPlant.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addPlant.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const newItem = {
            ...action.payload,
            isNewlyCreated: true,
          };
          state.data = [newItem, ...state.data];
        }
      })
      .addCase(addPlant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(editPlant.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editPlant.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.data.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editPlant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Maintenance Records
      .addCase(fetchMaintenanceRecordsByPlantId.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceRecordsByPlantId.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceRecords = action.payload;
      })
      .addCase(fetchMaintenanceRecordsByPlantId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchMaintenanceRecordsDetails.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceRecordsDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedMaintenanceRecord = action.payload;
      })
      .addCase(fetchMaintenanceRecordsDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addPlantMaintenanceRecord.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addPlantMaintenanceRecord.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(addPlantMaintenanceRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch plant by asset number (cell history)
      .addCase(fetchPlantByAssetNumber.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlantByAssetNumber.fulfilled, (state, action) => {
        state.loading = false;
        state.cellHistoryData = action.payload;
      })
      .addCase(fetchPlantByAssetNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addPlant.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(fetchPlantsWithFilters());
    listenerApi.dispatch(
      showToast({
        message: 'Plant saved successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addPlant.rejected, editPlant.rejected),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload,
        type: 'danger',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(editPlant.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: 'Plant updated successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addPlantMaintenanceRecord.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: 'Maintenance record saved successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addPlantMaintenanceRecord.rejected),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload || 'Failed to save maintenance record',
        type: 'danger',
      }),
    );
  },
});

redux.injectReducer(STORE_KEY, reducer);

// Export individual actions
export const { 
  setPlant, 
  setMaintenanceData, 
  clearMaintenanceData, 
  resetFilters, 
  setTileView 
} = actions;
